import React, { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { secureAuth } from '../utils/secureAuth'
import AuthModal from '../components/AuthModal'
import { sharepointServiceProduction } from '../utils/sharepointServiceProduction'
import { sharepointImageService } from '../utils/sharepointImageService'
import AdminProductsManager from '../components/AdminProductsManager'
import AdminDataDebugger from '../components/AdminDataDebugger'
import AdminCacheManager from '../components/AdminCacheManager'
import AdminShopifyManager from '../components/AdminShopifyManager'
import AdminStoreInfoManager from '../components/AdminStoreInfoManager'
import UnifiedSharePointManager from '../components/UnifiedSharePointManager'
import AdminArtGalleryManager from '../components/AdminArtGalleryManager'
import { contentService } from '../utils/contentService'
import { serverCacheService } from '../utils/serverCacheService'
import { kitOrderService } from '../services/kitOrderService'

function Admin() {
  const [user, setUser] = useState(secureAuth.getCurrentUser())
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'content' | 'store-info' | 'sharepoint-unified' | 'shopify' | 'art-gallery' | 'debug'>('dashboard')
  const [sharePointStatus, setSharePointStatus] = useState<'connecting' | 'connected' | 'failed' | 'disabled'>('connecting')
  const [isVerifying, setIsVerifying] = useState(true)

  // Verify authentication on mount (check JWT token server-side)
  useEffect(() => {
    const verifyAuth = async () => {
      setIsVerifying(true)
      const isAuthenticated = await secureAuth.verifyAuth()
      if (isAuthenticated) {
        setUser(secureAuth.getCurrentUser())
      } else {
        setUser(null)
      }
      setIsVerifying(false)
    }

    verifyAuth()
  }, [])

  // Subscribe to auth changes
  useEffect(() => {
    const unsubscribe = secureAuth.subscribe(() => {
      setUser(secureAuth.getCurrentUser())
    })
    return unsubscribe
  }, [])

  // Check SharePoint connection status on component mount
  useEffect(() => {
    const checkSharePointConnection = async () => {
      try {
        const result = await sharepointImageService.testConnection()
        if (result.success) {
          setSharePointStatus('connected')
        } else {
          setSharePointStatus('failed')
        }
      } catch (error) {
        setSharePointStatus('failed')
      }
    }
    
    checkSharePointConnection()
  }, [])

  // Auto-connect to SharePoint when admin panel loads - Latest version 2025-09-29
  useEffect(() => {
    const connectToSharePoint = async () => {
      try {
        console.log('Admin panel: Attempting SharePoint auto-connection...')
        const result = await sharepointServiceProduction.testConnection()
        if (result.success) {
          setSharePointStatus('connected')
          console.log('Admin panel: SharePoint connected successfully')
        } else {
          setSharePointStatus('failed')
          console.log('Admin panel: SharePoint connection failed:', result.message)
        }
      } catch (error) {
        setSharePointStatus('failed')
        console.log('Admin panel: SharePoint connection error:', error)
      }
    }

    // Only attempt connection if SharePoint is enabled
    const sharePointEnabled = import.meta.env.VITE_SHAREPOINT_ENABLED === 'true'
    if (sharePointEnabled) {
      connectToSharePoint()
    } else {
      setSharePointStatus('disabled')
    }
  }, [])

  // Show loading spinner while verifying authentication
  if (isVerifying) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Verifying authentication...</span>
          </div>
          <h4 className="text-muted">Verifying authentication...</h4>
        </div>
      </div>
    )
  }

  // Show login modal if not authenticated
  if (!user) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <h1 className="display-4 mb-4">🧜‍♀️ Admin Login</h1>
          <p className="lead mb-4">Please sign in to access the admin panel</p>
          <AuthModal isOpen={true} onClose={() => {}} />
        </div>
      </div>
    )
  }

  // Redirect non-admin users
  if (user && user.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  const ServerCacheControls = () => {
    const [cacheStatus, setCacheStatus] = useState({ isLoading: false, message: '' })

    const handleSaveToServer = async () => {
      setCacheStatus({ isLoading: true, message: 'Saving cache to server...' })
      try {
        const success = await serverCacheService.saveToServer()
        if (success) {
          setCacheStatus({ isLoading: false, message: 'Cache saved! Upload the downloaded file to /public/api/ and redeploy.' })
          setTimeout(() => setCacheStatus({ isLoading: false, message: '' }), 10000)
        } else {
          setCacheStatus({ isLoading: false, message: 'Failed to save cache' })
        }
      } catch (error) {
        setCacheStatus({ isLoading: false, message: 'Error saving cache' })
      }
    }

    const handleLoadFromServer = async () => {
      setCacheStatus({ isLoading: true, message: 'Loading from server...' })
      try {
        const success = await serverCacheService.forceReload()
        if (success) {
          setCacheStatus({ isLoading: false, message: 'Successfully loaded from server cache' })
          setTimeout(() => setCacheStatus({ isLoading: false, message: '' }), 5000)
        } else {
          setCacheStatus({ isLoading: false, message: 'No server cache found' })
        }
      } catch (error) {
        setCacheStatus({ isLoading: false, message: 'Error loading from server' })
      }
    }

    const status = serverCacheService.getStatus()

    return (
      <div className="d-flex align-items-center gap-2">
        <button 
          onClick={handleSaveToServer} 
          disabled={cacheStatus.isLoading}
          className="btn btn-success btn-sm"
          title="Save current cache data to server"
        >
          {cacheStatus.isLoading && cacheStatus.message.includes('Saving') ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            '💾 Save to Server'
          )}
        </button>
        
        <button 
          onClick={handleLoadFromServer} 
          disabled={cacheStatus.isLoading}
          className="btn btn-info btn-sm"
          title="Load cache data from server"
        >
          {cacheStatus.isLoading && cacheStatus.message.includes('Loading') ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Loading...
            </>
          ) : (
            '📥 Load from Server'
          )}
        </button>

        {status.isInitialized && status.hasData && (
          <span className="badge bg-success" title="Server cache loaded">✅</span>
        )}
        
        {cacheStatus.message && (
          <div className="alert alert-info alert-sm p-2 mb-0 ms-2" style={{ fontSize: '0.8rem' }}>
            {cacheStatus.message}
          </div>
        )}
      </div>
    )
  }

  const AdminDashboard = () => {
    const [dashboardData, setDashboardData] = useState({
      classes: { total: 0, active: 0, fullBooked: 0, totalEnrolled: 0, totalCapacity: 0 },
      kits: { total: 0, active: 0, inStock: 0, lowStock: 0, totalValue: 0 },
      gallery: { total: 0, available: 0, sold: 0 },
      images: { total: 0, categories: 0 },
      kitOrders: { total: 0, pending: 0, fulfilled: 0, totalRevenue: 0 }
    })

    const loadDashboardData = async () => {
      try {
        const classes = JSON.parse(localStorage.getItem('salty_mermaid_classes') || '[]')
        const kits = JSON.parse(localStorage.getItem('salty_mermaid_kits') || '[]')
        const gallery = JSON.parse(localStorage.getItem('salty_mermaid_gallery_items') || '[]')
        const images = JSON.parse(localStorage.getItem('salty_mermaid_images') || '[]')

        console.log('Dashboard: Loading data...', {
          kitsCount: kits.length,
          kitsData: kits
        })

        const classesActive = classes.filter((c: any) => c.availableSpots > 0).length
        const classesFullBooked = classes.filter((c: any) => c.availableSpots === 0).length
        const totalEnrolled = classes.reduce((sum: number, c: any) => sum + (c.totalSpots - c.availableSpots), 0)
        const totalCapacity = classes.reduce((sum: number, c: any) => sum + c.totalSpots, 0)

        // Art Kits statistics
        const kitsActive = kits.filter((k: any) => k.isActive).length
        const kitsInStock = kits.filter((k: any) => k.isActive && k.inStock && (k.stockQuantity || k.stockCount || 0) > 0).length
        const kitsLowStock = kits.filter((k: any) => k.isActive && k.inStock && (k.stockQuantity || k.stockCount || 0) <= 10 && (k.stockQuantity || k.stockCount || 0) > 0).length
        const totalValue = kits.reduce((sum: number, k: any) => sum + (k.isActive ? (k.price * (k.stockQuantity || k.stockCount || 0)) : 0), 0)

        console.log('Dashboard kits stats:', {
          total: kits.length,
          active: kitsActive,
          inStock: kitsInStock,
          lowStock: kitsLowStock,
          totalValue
        })

        const galleryAvailable = gallery.filter((g: any) => g.forSale).length
        const gallerySold = gallery.filter((g: any) => !g.forSale).length

        const categoriesCount = [...new Set(images.map((img: any) => img.category))].length

        // Kit Orders statistics (now async)
        const kitOrdersStats = await kitOrderService.getStats()

        setDashboardData({
          classes: {
            total: classes.length,
            active: classesActive,
            fullBooked: classesFullBooked,
            totalEnrolled,
            totalCapacity
          },
          kits: {
            total: kits.length,
            active: kitsActive,
            inStock: kitsInStock,
            lowStock: kitsLowStock,
            totalValue
          },
          gallery: {
            total: gallery.length,
            available: galleryAvailable,
            sold: gallerySold
          },
          images: {
            total: images.length,
            categories: categoriesCount
          },
          kitOrders: {
            total: kitOrdersStats.total,
            pending: kitOrdersStats.pending,
            fulfilled: kitOrdersStats.fulfilled,
            totalRevenue: kitOrdersStats.totalRevenue
          }
        })
      } catch (error) {
        console.error('Error loading dashboard data:', error)
      }
    }

    useEffect(() => {
      loadDashboardData()
      const interval = setInterval(loadDashboardData, 30000)
      return () => clearInterval(interval)
    }, [])

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fs-3 fw-bold">Dashboard Overview</h2>
          <div className="d-flex gap-2">
            <ServerCacheControls />
            <button onClick={loadDashboardData} className="btn btn-outline-secondary btn-sm">
              🔄 Refresh Data
            </button>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-md-6 col-lg-3">
            <div className="card shadow-sm border-danger">
              <div className="card-body">
                <h6 className="card-title text-danger">
                  🎨 Art Classes
                  <span className="badge bg-danger ms-2" style={{ fontSize: '0.5rem' }}>
                    <i className="fas fa-external-link-alt me-1"></i>Eventbrite
                  </span>
                </h6>
                <div className="small">
                  <div className="alert alert-info mb-0 py-2 px-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    Classes now managed via <strong>Eventbrite</strong>
                    <br />
                    <a href="https://www.eventbrite.com/organizations/events" target="_blank" rel="noopener noreferrer" className="alert-link">
                      Manage on Eventbrite →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="card-title text-warning">📦 Art Kits</h6>
                <div className="small text-muted">
                  {dashboardData.kits.total} total kits
                  <br />
                  {dashboardData.kits.active} active kits
                  <br />
                  {dashboardData.kits.inStock} in stock
                  <br />
                  {dashboardData.kits.lowStock} low stock
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <div className="card shadow-sm border-primary">
              <div className="card-body">
                <h6 className="card-title text-primary">🛒 Kit Orders</h6>
                <div className="small text-muted">
                  {dashboardData.kitOrders.total} total orders
                  <br />
                  <span className="text-warning">{dashboardData.kitOrders.pending} pending</span>
                  <br />
                  <span className="text-success">${dashboardData.kitOrders.totalRevenue.toFixed(2)} revenue</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-md-6 col-lg-3">
            <div className="card shadow-sm border-success">
              <div className="card-body">
                <h6 className="card-title text-success">
                  🖼️ Gallery
                  <span className="badge bg-success ms-2" style={{ fontSize: '0.5rem' }}>
                    <i className="fas fa-external-link-alt me-1"></i>Shopify
                  </span>
                </h6>
                <div className="small">
                  <div className="alert alert-info mb-0 py-2 px-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    Gallery items now from <strong>Shopify</strong>
                    <br />
                    <a href="https://saltymermaidart.com/admin" target="_blank" rel="noopener noreferrer" className="alert-link">
                      Manage on Shopify →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="card-title text-info">📷 Images</h6>
                <div className="small text-muted">
                  {dashboardData.images.total} total images
                  <br />
                  {dashboardData.images.categories} categories
                  <br />
                  Managed centrally
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Public Cache Management */}
        <div className="mt-4">
          <AdminCacheManager />
        </div>
      </div>
    )
  }

  const AdminContent = () => {
    const [editingContent, setEditingContent] = useState<any>(null)
    const [contentData, setContentData] = useState(contentService.getContent())

    useEffect(() => {
      // Listen for content updates
      const unsubscribe = contentService.subscribe(() => {
        setContentData(contentService.getContent())
      })
      return unsubscribe
    }, [])

    const handleSaveContent = (section: string, data: any) => {
      console.log('AdminContent: Saving content section:', section, data)
      contentService.updateSection(section as any, data)
      setEditingContent(null)
      
      // Trigger a storage event for other tabs/windows to pick up the change
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'salty_mermaid_content',
        newValue: JSON.stringify(contentService.getContent())
      }))
    }

    return (
      <div>
        <h2 className="fs-3 fw-bold mb-4">Content Management</h2>
        
        <div className="row g-4">
          <div className="col-md-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title">Site Content</h5>
                <div className="d-grid gap-3">
                  <button 
                    onClick={() => setEditingContent({ type: 'hero', data: contentData.hero })}
                    className="btn btn-outline-primary text-start p-3"
                  >
                    <div className="fw-medium">Homepage Hero Section</div>
                    <div className="small text-muted">Update main banner and welcome text</div>
                    <div className="small text-muted mt-1">
                      Last updated: {new Date(contentData.hero.lastUpdated).toLocaleDateString()}
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => setEditingContent({ type: 'about', data: contentData.about })}
                    className="btn btn-outline-success text-start p-3"
                  >
                    <div className="fw-medium">About Page Content</div>
                    <div className="small text-muted">Edit mission statement and story</div>
                    <div className="small text-muted mt-1">
                      Last updated: {new Date(contentData.about.lastUpdated).toLocaleDateString()}
                    </div>
                  </button>
                  
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title">Integration Settings</h5>
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                      <div>
                        <div className="fw-medium">Square Payment Processing</div>
                        <div className="small text-muted">Process payments and handle transactions</div>
                      </div>
                      <span className={`badge rounded-pill ${
                        contentData.settings.squareEnabled ? 'bg-success' : 'bg-danger'
                      }`}>
                        {contentData.settings.squareEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                      <div>
                        <div className="fw-medium">Shopify Integration</div>
                        <div className="small text-muted">Sync products and process orders</div>
                      </div>
                      <span className={`badge rounded-pill ${
                        contentData.settings.shopifyEnabled ? 'bg-success' : 'bg-danger'
                      }`}>
                        {contentData.settings.shopifyEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                      <div>
                        <div className="fw-medium">SharePoint Connection</div>
                        <div className="small text-muted">Inventory and document management</div>
                      </div>
                      <span className={`badge rounded-pill ${
                        sharePointStatus === 'connected' ? 'bg-success' : 
                        sharePointStatus === 'connecting' ? 'bg-warning' : 'bg-danger'
                      }`}>
                        {sharePointStatus === 'connected' ? 'Connected' : 
                         sharePointStatus === 'connecting' ? 'Connecting...' : 
                         sharePointStatus === 'failed' ? 'Connection Failed' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setEditingContent({ type: 'settings', data: contentData.settings })}
                  className="btn btn-primary w-100 mt-3"
                >
                  Configure Integrations
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Editing Modal */}
        {editingContent && (
          <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center" style={{zIndex: 999999}}>
            <div className="bg-white rounded w-100 shadow" style={{maxWidth: '768px', maxHeight: '90vh', overflowY: 'auto'}}>
              <div className="p-4">
                <h3 className="fs-4 fw-bold mb-4">
                  Edit {editingContent.type === 'hero' ? 'Homepage Hero' : 
                       editingContent.type === 'about' ? 'About Page' : 'Integration Settings'}
                </h3>

                {editingContent.type === 'hero' && (
                  <div className="mb-3">
                    <div className="mb-3">
                      <label className="form-label fw-medium">Main Title</label>
                      <input
                        type="text"
                        value={editingContent.data.title || ''}
                        onChange={(e) => setEditingContent({
                          ...editingContent,
                          data: { ...editingContent.data, title: e.target.value }
                        })}
                        className="form-control"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-medium">Subtitle</label>
                      <textarea
                        value={editingContent.data.subtitle || ''}
                        onChange={(e) => setEditingContent({
                          ...editingContent,
                          data: { ...editingContent.data, subtitle: e.target.value }
                        })}
                        rows={4}
                        className="form-control"
                      />
                    </div>
                  </div>
                )}

                {editingContent.type === 'about' && (
                  <div className="mb-3">
                    <div className="mb-3">
                      <label className="form-label fw-medium">Mission Statement</label>
                      <textarea
                        value={editingContent.data.mission || ''}
                        onChange={(e) => setEditingContent({
                          ...editingContent,
                          data: { ...editingContent.data, mission: e.target.value }
                        })}
                        rows={3}
                        className="form-control"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-medium">Our Story</label>
                      <textarea
                        value={editingContent.data.story || ''}
                        onChange={(e) => setEditingContent({
                          ...editingContent,
                          data: { ...editingContent.data, story: e.target.value }
                        })}
                        rows={4}
                        className="form-control"
                      />
                    </div>
                  </div>
                )}


                {editingContent.type === 'settings' && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded mb-3">
                      <div>
                        <div className="fw-medium">Square Payment Processing</div>
                        <div className="small text-muted">Enable Square payment processing</div>
                      </div>
                      <label className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          checked={editingContent.data.squareEnabled || false}
                          onChange={(e) => setEditingContent({
                            ...editingContent,
                            data: { ...editingContent.data, squareEnabled: e.target.checked }
                          })}
                          className="me-2"
                        />
                        Enable
                      </label>
                    </div>
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded mb-3">
                      <div>
                        <div className="fw-medium">Shopify Integration</div>
                        <div className="small text-muted">Enable Shopify product sync</div>
                      </div>
                      <label className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          checked={editingContent.data.shopifyEnabled || false}
                          onChange={(e) => setEditingContent({
                            ...editingContent,
                            data: { ...editingContent.data, shopifyEnabled: e.target.checked }
                          })}
                          className="me-2"
                        />
                        Enable
                      </label>
                    </div>
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                      <div>
                        <div className="fw-medium">SharePoint Connection</div>
                        <div className="small text-muted">Enable SharePoint integration</div>
                      </div>
                      <label className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          checked={editingContent.data.sharepointEnabled || false}
                          onChange={(e) => setEditingContent({
                            ...editingContent,
                            data: { ...editingContent.data, sharepointEnabled: e.target.checked }
                          })}
                          className="me-2"
                        />
                        Enable
                      </label>
                    </div>
                  </div>
                )}

                <div className="d-flex gap-3 pt-4 border-top mt-4">
                  <button
                    onClick={() => handleSaveContent(editingContent.type, editingContent.data)}
                    className="btn btn-primary flex-fill"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setEditingContent(null)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'dashboard': return <AdminDashboard />
      case 'products': return <AdminProductsManager />
      case 'content': return <AdminContent />
      case 'store-info': return <AdminStoreInfoManager />
      case 'sharepoint-unified': return <UnifiedSharePointManager />
      case 'shopify': return <AdminShopifyManager />
      case 'art-gallery': return <AdminArtGalleryManager />
      case 'debug': return <AdminDataDebugger />
      default: return <AdminDashboard />
    }
  }

  return (
    <div className="py-4">
      <div className="container-fluid">
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="display-5 fw-bold text-dark">Admin Panel</h1>
              <p className="text-muted">Welcome back, {user.name}</p>
            </div>
            
            {/* SharePoint Status Indicator */}
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">SharePoint:</span>
              <span className={`badge rounded-pill ${
                sharePointStatus === 'connected' ? 'bg-success' :
                sharePointStatus === 'connecting' ? 'bg-warning text-dark' :
                sharePointStatus === 'failed' ? 'bg-danger' :
                'bg-secondary'
              }`}>
                {sharePointStatus === 'connected' && '✅ Connected'}
                {sharePointStatus === 'connecting' && '🔄 Connecting...'}
                {sharePointStatus === 'failed' && '❌ Failed'}
                {sharePointStatus === 'disabled' && '⚪ Disabled'}
              </span>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <ul className="nav nav-pills nav-fill">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: '📊' },
              { id: 'products', label: 'Products', icon: '📦' },
              { id: 'shopify', label: 'Shopify', icon: '🛍️' },
              { id: 'art-gallery', label: 'Art Gallery', icon: '🖼️' },
              { id: 'content', label: 'Content', icon: '📝' },
              { id: 'store-info', label: 'Store Info', icon: '🏪' },
              { id: 'sharepoint-unified', label: 'SharePoint', icon: '☁️' },
              { id: 'debug', label: 'Debug', icon: '🔧' }
            ].map((tab) => (
              <li key={tab.id} className="nav-item">
                <button
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`nav-link ${
                    activeTab === tab.id ? 'active' : ''
                  }`}
                >
                  <span className="me-2">{tab.icon}</span>
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-light rounded p-4">
          {renderActiveTab()}
        </div>
      </div>
    </div>
  )
}

export default Admin